import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, ArrowLeft, Server, Copy, CheckCircle2, Lock } from 'lucide-react';
import { PageHeader, Section } from '../components/Scaffold';
import api from '../api/client';

/**
 * Cafe onboarding / provisioning wizard.
 *
 * Submits to POST /api/cafe/provision (superadmin-only) which creates the
 * owner + cafe + license + per-cafe database and RETURNS the generated
 * credentials, so the operator can hand them to the customer directly (the
 * welcome email is best-effort and may be suppressed in prod).
 */

// Where the onboarded cafe owner logs in (the cafe-admin app). This value is shown
// on the confirmation screen and read aloud to the customer, so it MUST match the
// address the provisioning welcome email sends them to (backend/app/utils/email.py)
// and the one the Installation Guide tells technicians to open. It previously
// hardcoded the raw Azure Static Web Apps hostname, which sent customers somewhere
// different from every other channel.
const ADMIN_URL = import.meta.env.VITE_ADMIN_PORTAL_URL || 'https://nolag.hsntechnologies.com';

const EMPTY = {
  cafeName: '',
  location: '',
  phone: '',
  ownerName: '',
  ownerEmail: '',
  maxPcs: 10,
  validityDays: 30,
};

// Loose shape check before asking the server; the server does the real match.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// "Add café for this owner" on CafeDetail opens this page with the owner in
// router state: { name, email, phone, fromCafeId, fromCafeName }. Email and
// name are LOCKED — provisioning matches the owner by email, so an edited email
// would silently create (or pick) a different account. The phone is only a
// starting value: it becomes the NEW cafe's contact number (Cafe.phone), and a
// branch often has its own.
function initialForm(prefill) {
  if (!prefill) return EMPTY;
  return {
    ...EMPTY,
    ownerName: prefill.name || '',
    ownerEmail: prefill.email || '',
    phone: prefill.phone || '',
  };
}

// noLag rebrand: no Figma node exists for this onboarding wizard (it's a
// utility form, not one of the designed screens), and it has no per-page
// <style> block of its own — it's built entirely from shared scaffold
// components (Section/PageHeader) and generic .page/.card/.field classes
// in index.css. Scoping just the accent variable here (not reskinning
// .card, which has no design reference to verify against) still flows
// automatically into .btn--primary and .field__input:focus, which already
// read var(--accent-primary) in index.css.
const ONBOARD_ACCENT_STYLE = `
  .cafe-onboard {
    --accent-primary: #FE7B00;
    --accent-primary-rgb: 254, 123, 0;
    --accent-primary-glow: rgba(254, 123, 0, 0.25);
    --accent-primary-subtle: rgba(254, 123, 0, 0.12);
    --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);
  }
`;

// The owner-email check line and the locked owner fields.
const ONBOARD_FORM_STYLE = `
  .cafe-onboard .field__input[readonly] { opacity: 0.7; cursor: not-allowed; }
  .onboard-hint { margin: 0; font-size: var(--text-xs); line-height: 1.5; color: var(--text-secondary); }
  .onboard-hint--ok { color: var(--status-success, #22c55e); }
  .onboard-hint--warn { color: var(--status-warning, #f59e0b); }
  .onboard-promote { display: flex; align-items: flex-start; gap: 8px; margin-top: 6px; color: var(--text-primary); cursor: pointer; }
  .onboard-promote input { margin-top: 2px; }
  .onboard-locked { display: flex; align-items: center; gap: 6px; margin: 0 0 12px; font-size: var(--text-xs); color: var(--text-secondary); }
`;

export default function CafeOnboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [prefill] = useState(() => location.state?.prefillOwner || null);
  const [form, setForm] = useState(() => initialForm(prefill));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  // Live owner-email check: null, or { email, status: 'checking' | 'done' | 'error', data }.
  const [emailCheck, setEmailCheck] = useState(null);
  const [promote, setPromote] = useState(false);
  // What the check filled in from an existing owner's account ({ name, phone }),
  // so a changed email can take it back out again where it is still untouched.
  // The ref mirrors it for checkEmail, which is created once.
  const [autofill, setAutofill] = useState(null);
  const autofillRef = useRef(null);
  const checkTimer = useRef(null);
  const checkSeq = useRef(0);

  const lockedEmail = prefill?.email || '';
  const nameLocked = Boolean(lockedEmail && prefill?.name);
  const backTo = prefill?.fromCafeId ? `/cafes/${prefill.fromCafeId}` : '/cafes';
  const backLabel = prefill?.fromCafeId ? `Back to ${prefill.fromCafeName || 'café'}` : 'Back to Registry';

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Ask the server what provisioning would do with this email. Only the newest
  // request may update the screen (checkSeq), so a slow answer for text that has
  // since changed is dropped.
  const checkEmail = useCallback(async (raw) => {
    clearTimeout(checkTimer.current);
    const email = raw.trim().toLowerCase();
    const seq = ++checkSeq.current;
    if (!EMAIL_RE.test(email)) {
      setEmailCheck(null);
      return;
    }
    setEmailCheck({ email, status: 'checking' });
    let data;
    try {
      ({ data } = await api.get('/internal/owner-lookup', { params: { email } }));
    } catch {
      // Not fatal: provisioning enforces the same rules and says why.
      if (seq === checkSeq.current) setEmailCheck({ email, status: 'error' });
      return;
    }
    if (seq !== checkSeq.current) return;
    setEmailCheck({ email, status: 'done', data });
    // An existing owner keeps the name on their account (provisioning ignores a
    // typed one), and their own number is the natural starting point for the
    // new café's contact phone — so fill both in. The phone is only filled when
    // empty or still holding an earlier autofill, never over something typed.
    if (data.exists && data.reuse) {
      const prev = autofillRef.current;
      const next = { name: data.name || '', phone: data.phone || '' };
      autofillRef.current = next;
      setAutofill(next);
      setForm((f) => ({
        ...f,
        ownerName: next.name || f.ownerName,
        phone: next.phone && (!f.phone || (prev && f.phone === prev.phone)) ? next.phone : f.phone,
      }));
    }
  }, []);

  const onEmailChange = (e) => {
    const { value } = e.target;
    // Take back out whatever the last check filled in, unless it was edited.
    const prev = autofillRef.current;
    autofillRef.current = null;
    setAutofill(null);
    setForm((f) => ({
      ...f,
      ownerEmail: value,
      ownerName: prev?.name && f.ownerName === prev.name ? '' : f.ownerName,
      phone: prev?.phone && f.phone === prev.phone ? '' : f.phone,
    }));
    setPromote(false);
    setEmailCheck(null);
    checkSeq.current += 1;
    clearTimeout(checkTimer.current);
    checkTimer.current = setTimeout(() => checkEmail(value), 600);
  };

  const onEmailBlur = () => {
    const email = form.ownerEmail.trim().toLowerCase();
    if (emailCheck?.email === email && emailCheck.status !== 'error') return;
    checkEmail(form.ownerEmail);
  };

  // A locked (prefilled) email is checked straight away, so the page opens
  // already saying which cafés this owner has.
  useEffect(() => {
    if (lockedEmail) checkEmail(lockedEmail);
  }, [lockedEmail, checkEmail]);

  useEffect(() => () => clearTimeout(checkTimer.current), []);

  const verdict = emailCheck?.status === 'done' ? emailCheck.data : null;
  // An existing account that provisioning will not use as it is.
  const blocked = Boolean(verdict?.exists && !verdict.reuse);
  // An existing owner keeps their account's name, so show that one, locked.
  const nameFromAccount = Boolean(verdict?.reuse && verdict.name);
  const phoneIsOwners = Boolean(
    (prefill?.phone && form.phone === prefill.phone) || (autofill?.phone && form.phone === autofill.phone),
  );

  async function handleSubmit() {
    setError('');
    if (!form.cafeName || !form.location || !form.ownerName || !form.ownerEmail || !form.phone) {
      setError('Please fill in cafe name, location, contact phone, owner name and owner email.');
      return;
    }
    if (blocked && !(verdict.can_promote && promote)) {
      setError(verdict.message || 'This email cannot be used for a café owner.');
      return;
    }
    const promotedKind = blocked ? verdict.account_type : null;
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.ownerName,
        email: form.ownerEmail,
        preferred_username: form.ownerEmail.split('@')[0],
        cafe_name: form.cafeName,
        cafe_location: form.location,
        pc_count: Number(form.maxPcs) || 1,
        mobile_number: form.phone,
        accept_terms: true,
        validity_days: Number(form.validityDays) || 30,
        promote_existing_account: Boolean(promotedKind),
      };
      const { data } = await api.post('/cafe/provision', payload);
      setResult({ ...data, promotedKind });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || err?.message || 'Onboarding failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const startAnother = () => {
    setResult(null);
    setError('');
    setPromote(false);
    setEmailCheck(null);
    autofillRef.current = null;
    setAutofill(null);
    setForm(initialForm(prefill));
    if (lockedEmail) checkEmail(lockedEmail);
  };

  if (result) {
    return (
      <div className="page cafe-onboard">
        <style>{ONBOARD_ACCENT_STYLE}</style>
        <PageHeader
          title="Café provisioned"
          subtitle="Hand these credentials to the customer. They should change the password on first login."
          actions={
            <button className="btn btn--ghost" onClick={() => navigate(backTo)}>
              <ArrowLeft size={16} /> {backLabel}
            </button>
          }
        />
        <div className="card">
          <Section title={`${result.cafe_name} (cafe #${result.cafe_id})`} desc="Per-cafe database provisioned">
            <CredRow label="Admin URL" value={ADMIN_URL} />
            <CredRow label="Login (email)" value={result.owner_email} />
            {result.owner_existing ? (
              <div className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                <span className="field__label" style={{ minWidth: 180 }}>Owner</span>
                <span style={{ flex: 1, opacity: 0.85 }}>
                  {result.promotedKind
                    ? `Existing ${result.promotedKind} account — now a café owner. They log in to the admin app with the password they already use.`
                    : 'Existing owner — this cafe was added to their account; they log in with their current password.'}
                </span>
              </div>
            ) : (
              <CredRow label="Temporary password" value={result.temp_password} />
            )}
            <CredRow label="License key (for kiosk PCs)" value={result.license_key} />
            <CredRow label="Max PCs" value={String(result.max_pcs)} />
          </Section>
          <div className="page-actions" style={{ marginTop: 8 }}>
            <button className="btn btn--secondary" onClick={startAnother}>
              {prefill ? 'Add another café for this owner' : 'Onboard another'}
            </button>
            <button className="btn btn--primary" onClick={() => navigate('/cafes')}>
              <CheckCircle2 size={16} /> Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page cafe-onboard">
      <style>{ONBOARD_ACCENT_STYLE + ONBOARD_FORM_STYLE}</style>
      <PageHeader
        title={prefill ? 'Add another café for this owner' : 'Onboard a Café'}
        subtitle={
          prefill
            ? `Adds a new café under ${prefill.name || prefill.email} — the owner details are locked`
            : 'Provision a new tenant — create the cafe, its owner, license and per-cafe database'
        }
        actions={
          <button className="btn btn--ghost" onClick={() => navigate(backTo)}>
            <ArrowLeft size={16} /> {backLabel}
          </button>
        }
      />

      {error && (
        <div className="card" style={{ borderColor: 'var(--danger, #c0392b)', marginBottom: 12 }}>
          <span style={{ color: 'var(--danger, #c0392b)' }}>{error}</span>
        </div>
      )}

      <div className="card">
        <Section title="Café details" desc="Stored in the global cafe registry (cafes table)">
          <div className="form-grid">
            <div className="field">
              <label className="field__label">Café name</label>
              <input className="field__input" placeholder="e.g. NoLag Indiranagar" value={form.cafeName} onChange={set('cafeName')} />
            </div>
            <div className="field">
              <label className="field__label">Location</label>
              <input className="field__input" placeholder="City / area" value={form.location} onChange={set('location')} />
            </div>
            <div className="field">
              <label className="field__label">Contact phone</label>
              <input className="field__input" placeholder="+91 ..." value={form.phone} onChange={set('phone')} />
              {phoneIsOwners && (
                <p className="onboard-hint">This is the owner&apos;s own number — change it if this café has its own.</p>
              )}
            </div>
          </div>
        </Section>

        <Section
          title="Owner account"
          desc={lockedEmail ? 'The new café is added to this existing owner' : 'Creates the cafe-admin user and maps them to this cafe'}
        >
          {lockedEmail && (
            <p className="onboard-locked">
              <Lock size={12} /> Name and email are locked — a different email would be a different account.
              To use another owner, go back and add the café from the Cafés page.
            </p>
          )}
          <div className="form-grid">
            <div className="field">
              <label className="field__label">Owner name</label>
              <input
                className="field__input"
                placeholder="Full name"
                value={form.ownerName}
                onChange={set('ownerName')}
                readOnly={nameLocked || nameFromAccount}
              />
              {nameFromAccount && !nameLocked && (
                <p className="onboard-hint">Filled in from their existing account.</p>
              )}
            </div>
            <div className="field">
              <label className="field__label">Owner email</label>
              <input
                className="field__input"
                type="email"
                placeholder="owner@example.com"
                value={form.ownerEmail}
                onChange={onEmailChange}
                onBlur={onEmailBlur}
                readOnly={Boolean(lockedEmail)}
              />
              <EmailVerdict check={emailCheck} promote={promote} onPromoteChange={setPromote} />
            </div>
          </div>
          {!verdict?.exists && (
            <p className="field__label" style={{ marginTop: 8, opacity: 0.7 }}>
              A temporary password is auto-generated and shown after provisioning.
            </p>
          )}
        </Section>

        <Section title="License" desc="An initial license is issued at provisioning time">
          <div className="form-grid">
            <div className="field">
              <label className="field__label">Max PCs</label>
              <input className="field__input" type="number" min="1" value={form.maxPcs} onChange={set('maxPcs')} />
            </div>
            <div className="field">
              <label className="field__label">License validity (days)</label>
              <input className="field__input" type="number" min="1" value={form.validityDays} onChange={set('validityDays')} />
            </div>
          </div>
        </Section>

        <Section title="Provisioning" desc="Creates the dedicated per-cafe database">
          <span className="field__label" style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: 0.7 }}>
            <Server size={14} /> A per-cafe database is created and seeded automatically.
          </span>
        </Section>

        <div className="page-actions" style={{ marginTop: 8 }}>
          <button className="btn btn--secondary" onClick={() => navigate(backTo)} disabled={submitting}>Cancel</button>
          <button className="btn btn--primary" onClick={handleSubmit} disabled={submitting}>
            <Building2 size={16} /> {submitting ? 'Provisioning…' : 'Create & Provision Café'}
          </button>
        </div>
      </div>
    </div>
  );
}

// One line under the owner email saying what provisioning will do with it —
// the same verdict POST /cafe/provision enforces (GET /internal/owner-lookup).
function EmailVerdict({ check, promote, onPromoteChange }) {
  if (!check || check.status === 'error') return null;
  if (check.status === 'checking') return <p className="onboard-hint">Checking this email…</p>;
  const d = check.data;
  if (!d.exists) {
    return <p className="onboard-hint onboard-hint--ok">New owner — a new login will be created.</p>;
  }
  if (d.reuse) {
    const names = (d.owned_cafes || []).map((c) => c.name).join(', ');
    return (
      <p className="onboard-hint">
        {names
          ? `This email already owns: ${names}.`
          : `This email belongs to an existing admin account${d.name ? ` (${d.name})` : ''}.`}
        {' '}The new café is added to their account — they keep their current password.
      </p>
    );
  }
  const staffAt = d.account_type === 'staff' && d.staff_cafe ? d.staff_cafe.name : null;
  return (
    <div>
      <p className="onboard-hint onboard-hint--warn">{d.message}</p>
      {d.can_promote && (
        <label className="onboard-promote">
          <input type="checkbox" checked={promote} onChange={(e) => onPromoteChange(e.target.checked)} />
          <span className="onboard-hint" style={{ color: 'inherit' }}>
            Turn this {d.account_type === 'staff' ? 'staff' : 'customer'} account{d.name ? ` (${d.name})` : ''} into
            a café owner.{staffAt ? ` They will stop being staff at ${staffAt}.` : ''} They keep their current password.
          </span>
        </label>
      )}
    </div>
  );
}

function CredRow({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <div className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
      <span className="field__label" style={{ minWidth: 180 }}>{label}</span>
      <code style={{ flex: 1, userSelect: 'all' }}>{value}</code>
      <button className="btn btn--ghost" onClick={copy} title="Copy">
        {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
      </button>
    </div>
  );
}
